import ReactDOMServer from 'react-dom/server.browser';
import React from 'react';
import type { WebsiteData } from '../types';

import { NavbarTemplate } from '../components/templates/NavbarTemplate';
import { HeroTemplate } from '../components/templates/HeroTemplate';
import { AboutTemplate } from '../components/templates/AboutTemplate';
import { ServicesTemplate } from '../components/templates/ServicesTemplate';
import { ContactTemplate } from '../components/templates/ContactTemplate';
import { FooterTemplate } from '../components/templates/FooterTemplate';
import { LoginTemplate } from '../components/templates/LoginTemplate';

// Helper to add data-path attributes to React elements
const addDataPaths = (data: any, pathPrefix = ''): any => {
    if (typeof data !== 'object' || data === null || React.isValidElement(data)) {
        return data;
    }

    if (Array.isArray(data)) {
        return data.map((item, index) => addDataPaths(item, `${pathPrefix}[${index}]`));
    }

    const newData: { [key: string]: any } = {};
    for (const key in data) {
        const newPath = pathPrefix ? `${pathPrefix}.${key}` : key;
        if (typeof data[key] === 'string' && key !== 'href' && key !== 'imageUrl' && key !== 'icon' && key !== 'url' && key !== 'platform') {
            // This is a simple way to wrap editable strings.
            // In a real app, you'd integrate this into your templates more deeply.
            // This is a conceptual example. The actual implementation is in the templates themselves.
        } else {
            newData[key] = addDataPaths(data[key], newPath);
        }
    }
    return newData;
};

// We will add the data-path attributes directly in the templates for more control.
const renderComponent = (Component: React.FC<any>, props: any) => {
    return ReactDOMServer.renderToStaticMarkup(React.createElement(Component, props));
};

const liveEditScript = `
  <script>
    window.addEventListener('message', (event) => {
      if (event.data.type === 'TOGGLE_EDIT_MODE') {
        const isEditing = event.data.payload;
        document.querySelectorAll('[data-path]').forEach(el => {
          el.setAttribute('contenteditable', isEditing);
          el.style.outline = isEditing ? '2px dashed #FF6F61' : 'none';
          el.style.cursor = isEditing ? 'text' : 'default';

          if (isEditing) {
            el.addEventListener('blur', handleBlur);
          } else {
            el.removeEventListener('blur', handleBlur);
          }
        });
      }
    });

    function handleBlur(e) {
      const path = e.target.getAttribute('data-path');
      const content = e.target.innerText;
      window.parent.postMessage({
        type: 'CONTENT_UPDATE',
        payload: { path, content }
      }, '*');
    }
  </script>
`;

// Generate HTML for login pages
const generateLoginPageHtml = (data: WebsiteData, fontHeading: string, fontBody: string, fontFamilies: string): string => {
  const { theme, customCss } = data;

  const loginHtml = renderComponent(LoginTemplate, {
    brandName: data.navbar.brandName,
    headline: data.hero.headline,
    subheadline: data.hero.subheadline,
    showSocialLogin: true,
    showSignupLink: true,
    backgroundColor: theme.secondary + '1A',
    formBackgroundColor: theme.base
  });

  return `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>${data.navbar.brandName} | Built with AJ STUDIOZ</title>
  <meta name="description" content="${data.hero.subheadline} - Powered by AJ STUDIOZ Creative Suite" />
  <meta name="generator" content="AJ STUDIOZ Creative Suite" />
  <link rel="icon" type="image/svg+xml" href="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='32' height='32' viewBox='0 0 32 32' fill='none'%3E%3Cpath d='M16 2L2 9L16 16L30 9L16 2Z' fill='%23FF6B6B'/%3E%3Cpath d='M2 23L16 30L30 23' stroke='%23FF6B6B' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3Cpath d='M2 16L16 23L30 16' stroke='%23FF6B6B' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E" />
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
  <link href="https://fonts.googleapis.com/css2?family=${fontFamilies}&display=swap" rel="stylesheet">
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body {
      font-family: '${fontBody}', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      margin: 0;
      padding: 0;
    }
    h1, h2, h3, h4, h5, h6 {
      font-family: '${fontHeading}', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    }
    ${customCss || ''}
  </style>
</head>
<body>
  ${loginHtml}
  
  <!-- AJ STUDIOZ Branding -->
  <div style="
    position: fixed;
    bottom: 20px;
    right: 20px;
    z-index: 9999;
    background: rgba(255,255,255,0.95);
    backdrop-filter: blur(10px);
    padding: 8px 16px;
    border-radius: 50px;
    box-shadow: 0 4px 20px rgba(0,0,0,0.1);
    display: flex;
    align-items: center;
    gap: 8px;
    transition: all 0.3s ease;
    cursor: pointer;
    border: 1px solid rgba(255,107,107,0.2);
  " 
  onmouseover="this.style.transform='translateY(-2px)'"
  onmouseout="this.style.transform='translateY(0)'"
  onclick="window.open('https://aj-studioz-creative-suite.vercel.app', '_blank')"
  title="Built with AJ STUDIOZ Creative Suite">
    <img 
      src="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 24 24' fill='none'%3E%3Cpath d='M12 2L2 7L12 12L22 7L12 2Z' fill='%23FF6B6B'/%3E%3Cpath d='M2 17L12 22L22 17' stroke='%23FF6B6B' stroke-width='2'/%3E%3Cpath d='M2 12L12 17L22 12' stroke='%23FF6B6B' stroke-width='2'/%3E%3C/svg%3E"
      alt="AJ STUDIOZ" 
      style="width: 20px; height: 20px;"
    />
    <div style="font-size: 10px; font-weight: 700; color: #FF6B6B; text-transform: uppercase;">Built with</div>
    <div style="font-size: 12px; font-weight: 800; background: linear-gradient(135deg, #FF6B6B 0%, #FF8E53 100%); -webkit-background-clip: text; -webkit-text-fill-color: transparent;">AJ STUDIOZ</div>
  </div>
</body>
</html>
  `.trim();
};

export const generateFullHtml = (data: WebsiteData): string => {
  const { theme, groundingSources, customCss } = data;

  const fontHeading = theme.fontHeading || 'Poppins';
  const fontBody = theme.fontBody || 'Lato';
  const fontFamilies = [...new Set([fontHeading, fontBody])].map(font => `${font.replace(' ', '+')}:wght@400;500;600;700;800`).join('&family=');

  // Detect if this is a login/auth page
  const isLoginPage = data.navbar.brandName.toLowerCase().includes('login') || 
                      data.hero.headline.toLowerCase().includes('login') ||
                      data.hero.headline.toLowerCase().includes('sign in') ||
                      data.hero.headline.toLowerCase().includes('welcome back');

  // If it's a login page, render login template instead
  if (isLoginPage) {
    return generateLoginPageHtml(data, fontHeading, fontBody, fontFamilies);
  }

  // AJ STUDIOZ Branding Watermark (Lovable-style)
  const ajBrandingHtml = `
    <div style="
      position: fixed;
      top: 20px;
      left: 20px;
      z-index: 9999;
      background: linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(248,248,248,0.95) 100%);
      backdrop-filter: blur(10px);
      padding: 8px 16px;
      border-radius: 50px;
      box-shadow: 0 4px 20px rgba(0,0,0,0.1), 0 0 0 1px rgba(0,0,0,0.05);
      display: flex;
      align-items: center;
      gap: 10px;
      transition: all 0.3s ease;
      cursor: pointer;
      border: 1px solid rgba(255,107,107,0.2);
    " 
    onmouseover="this.style.transform='translateY(-2px)'; this.style.boxShadow='0 6px 25px rgba(0,0,0,0.15), 0 0 0 1px rgba(255,107,107,0.3)';"
    onmouseout="this.style.transform='translateY(0)'; this.style.boxShadow='0 4px 20px rgba(0,0,0,0.1), 0 0 0 1px rgba(0,0,0,0.05)';"
    onclick="window.open('https://aj-studioz-creative-suite.vercel.app', '_blank')"
    title="Built with AJ STUDIOZ Creative Suite">
      <img 
        src="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none'%3E%3Cpath d='M12 2L2 7L12 12L22 7L12 2Z' fill='%23FF6B6B'/%3E%3Cpath d='M2 17L12 22L22 17' stroke='%23FF6B6B' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3Cpath d='M2 12L12 17L22 12' stroke='%23FF6B6B' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E"
        alt="AJ STUDIOZ" 
        style="width: 24px; height: 24px; filter: drop-shadow(0 2px 4px rgba(0,0,0,0.1));"
      />
      <div style="display: flex; flex-direction: column; line-height: 1;">
        <span style="
          font-family: '${fontHeading}', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          font-size: 11px;
          font-weight: 700;
          color: #FF6B6B;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        ">Built with</span>
        <span style="
          font-family: '${fontHeading}', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          font-size: 14px;
          font-weight: 800;
          background: linear-gradient(135deg, #FF6B6B 0%, #FF8E53 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          letter-spacing: 0.3px;
        ">AJ STUDIOZ</span>
      </div>
    </div>
  `;

  const groundingSourcesHtml = groundingSources && groundingSources.length > 0 ? `
    <section id="sources" style="padding: 3rem 0; background-color: ${theme.secondary}1A;">
      <div style="max-width: 1280px; margin: 0 auto; padding: 0 1.5rem;">
        <h2 style="font-size: 1.875rem; font-weight: 700; color: ${theme.neutral}; margin-bottom: 1.5rem; text-align: center;">
          Content Sources
        </h2>
        <ul style="list-style: none; max-width: 56rem; margin: 0 auto; color: ${theme.neutral}CC; display: flex; flex-direction: column; gap: 0.75rem;">
          ${groundingSources.map(source =>
            source.web && source.web.uri ? `
              <li style="background-color: ${theme.base}; padding: 0.75rem; border-radius: 0.375rem; box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);">
                <a
                  href="${source.web.uri}"
                  target="_blank"
                  rel="noopener noreferrer"
                  style="color: ${theme.primary}; text-decoration: none;"
                  title="${source.web.uri}"
                >
                  ${source.web.title || 'Untitled Source'}
                </a>
              </li>
            ` : ''
          ).join('')}
        </ul>
      </div>
    </section>
  ` : '';

  const html = `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title data-path="navbar.brandName">${data.navbar.brandName} | Built with AJ STUDIOZ</title>
  <meta name="description" content="${data.hero.subheadline || data.navbar.brandName} - Powered by AJ STUDIOZ Creative Suite" />
  <meta name="generator" content="AJ STUDIOZ Creative Suite" />
  <link rel="icon" type="image/svg+xml" href="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='32' height='32' viewBox='0 0 32 32' fill='none'%3E%3Cpath d='M16 2L2 9L16 16L30 9L16 2Z' fill='%23FF6B6B'/%3E%3Cpath d='M2 23L16 30L30 23' stroke='%23FF6B6B' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3Cpath d='M2 16L16 23L30 16' stroke='%23FF6B6B' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E" />
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
  <link href="https://fonts.googleapis.com/css2?family=${fontFamilies}&display=swap" rel="stylesheet">
  <script src="https://cdn.tailwindcss.com"></script>
  <script>
    tailwind.config = {
      theme: {
        extend: {
          fontFamily: {
            sans: ['${fontBody}', 'sans-serif'],
            heading: ['${fontHeading}', 'sans-serif'],
          },
          colors: {
            primary: 'var(--color-primary)',
            secondary: 'var(--color-secondary)',
            accent: 'var(--color-accent)',
            neutral: 'var(--color-neutral)',
            'base-100': 'var(--color-base-100)',
          }
        }
      }
    }
  </script>
  <style>
    :root {
      --color-primary: ${theme.primary};
      --color-secondary: ${theme.secondary};
      --color-accent: ${theme.accent};
      --color-neutral: ${theme.neutral};
      --color-base-100: ${theme.base};
    }
    body {
      font-family: '${fontBody}', sans-serif;
    }
    h1, h2, h3, h4, h5, h6 {
      font-family: '${fontHeading}', sans-serif;
    }
    [contenteditable="true"]:hover {
        box-shadow: 0 0 0 2px #FFDAB9;
    }
    ${customCss || ''}
  </style>
</head>
<body class="bg-base-100 text-neutral">
  ${ajBrandingHtml}
  ${renderComponent(NavbarTemplate, data.navbar)}
  ${renderComponent(HeroTemplate, data.hero)}
  ${renderComponent(AboutTemplate, data.about)}
  ${renderComponent(ServicesTemplate, data.services)}
  ${renderComponent(ContactTemplate, data.contact)}
  ${groundingSourcesHtml}
  ${renderComponent(FooterTemplate, data.footer)}
  ${liveEditScript}
</body>
</html>
  `;

  return html.trim();
};